import type { TrialRecap } from '@/hooks/useTrialRecap';
import { Button } from '@/components/ui/button';
import { ScanLine } from 'lucide-react';

interface Props {
  recap: TrialRecap | null;
  onContinue: () => void;
}

function formatDuration(seconds: number) {
  const total = Math.max(0, Math.round(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (h === 0 && m === 0) return 'a few seconds';
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

export function PageTimeSaved({ recap, onContinue }: Props) {
  const scans = recap?.scans.total ?? 0;
  const saved = formatDuration(recap?.scans.secondsSaved ?? 0);

  return (
    <div className="min-h-full flex flex-col px-6 py-12 max-w-lg mx-auto">
      <p className="text-[10px] uppercase tracking-[0.3em] text-primary/70 mb-3">
        Chapter one
      </p>
      <h1 className="text-3xl sm:text-4xl font-display font-semibold tracking-tight mb-6">
        I scanned my way<br />out of typing.
      </h1>

      <div className="flex items-center gap-4 mb-8">
        <div className="h-14 w-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
          <ScanLine className="h-6 w-6 text-primary" />
        </div>
        <div>
          <p className="text-5xl font-bold tracking-tight">{scans}</p>
          <p className="text-xs text-muted-foreground/70 uppercase tracking-widest">
            receipts scanned
          </p>
        </div>
      </div>

      <div className="rounded-2xl border border-border/30 bg-muted/5 p-5 mb-6">
        <p className="text-sm text-muted-foreground/80 leading-relaxed">
          Every receipt tapped, itemised and converted automatically. That
          saved me roughly
        </p>
        <p className="text-3xl font-semibold tracking-tight mt-2">
          {saved}
          <span className="text-sm font-normal text-muted-foreground/60 ms-1">
            vs typing
          </span>
        </p>
      </div>

      <p className="text-xs text-muted-foreground/60 leading-relaxed mb-8">
        On the free plan I keep 5 scans per month. The rest returns to manual
        entry.
      </p>

      <div className="mt-auto pt-6">
        <Button onClick={onContinue} className="w-full" size="lg">
          See what else I tracked
        </Button>
      </div>
    </div>
  );
}
